// src/hooks/useIdleTimeout.js
//
// BUG-002 / RM_32: the SPA had no client-side inactivity detection — it only
// expired when the Auth0 access token itself expired, which is a server/
// token-driven lifetime, not a measure of whether the user is actually still
// at the keyboard. This hook adds an independent, purely client-side idle
// timer: it listens for real interaction (mouse, keyboard, touch, scroll)
// and calls `onIdle` once `timeoutMs` has elapsed with none of it.
//
// Usage (mount once, at the top level — see App.js):
//   useIdleTimeout(15 * 60 * 1000, () => { if (isAuthenticated) logout(...) });

import { useEffect, useRef, useCallback } from 'react';

// Events that count as "the user is here". Deliberately excludes things like
// visibilitychange alone — a tab merely being refocused isn't interaction.
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

export function useIdleTimeout(timeoutMs, onIdle) {
  const timerRef = useRef(null);

  // Keep the latest onIdle without having to re-bind listeners every time
  // the caller's callback identity changes (e.g. isAuthenticated flipping).
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      onIdleRef.current();
    }, timeoutMs);
  }, [timeoutMs]);

  useEffect(() => {
    resetTimer();

    ACTIVITY_EVENTS.forEach((evt) =>
      window.addEventListener(evt, resetTimer, { passive: true })
    );

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach((evt) =>
        window.removeEventListener(evt, resetTimer)
      );
    };
  }, [resetTimer]);
}